/**
 * Поддержание SIP-регистрации и обработка её потери.
 *
 * Живой веб-сокет ещё не значит, что диспетчер может звонить: регистрация может
 * протухнуть при живом транспорте, и это самый неприятный отказ — индикатор зелёный,
 * а звонки не проходят. Поэтому re-REGISTER идёт по своему таймеру на 50% от expires,
 * а состояние «не Registered дольше 60 с» отдельно объявляется потерей линии:
 * по ней сервер снимает раздачу с диспетчера (dispatcherCapacity → blockedReason='no_sip').
 */
import { systemScheduler } from './ports.js';
/** Спека §7.9: expires=120 с, обновление на половине таймера */
export const REGISTER_EXPIRES_SEC = 120;
export const REGISTER_REFRESH_RATIO = 0.5;
/** Через сколько отсутствие регистрации считается потерей линии */
export const LINE_DOWN_AFTER_MS = 60_000;
export class RegistererSupervisor {
    #registerer;
    #options;
    #scheduler;
    #expiresSec;
    #unsubscribe = null;
    #cancelRefresh = null;
    #cancelLineDown = null;
    #running = false;
    #lineDown = false;
    constructor(registerer, options) {
        this.#registerer = registerer;
        this.#options = options;
        this.#scheduler = options.scheduler ?? systemScheduler;
        this.#expiresSec = options.expiresSec ?? REGISTER_EXPIRES_SEC;
    }
    isRegistered() {
        return this.#registerer.getState() === 'registered';
    }
    /** Объявлена ли линия потерянной — пульт показывает «нет линии» */
    isLineDown() {
        return this.#lineDown;
    }
    start() {
        if (this.#running)
            return;
        this.#running = true;
        this.#unsubscribe = this.#registerer.onStateChange((state) => {
            this.#options.onState?.(state);
            if (state === 'registered')
                this.#onRegistered();
            else
                this.#onNotRegistered();
        });
        void this.#register();
    }
    stop() {
        this.#running = false;
        this.#clearTimers();
        this.#unsubscribe?.();
        this.#unsubscribe = null;
        void this.#registerer.unregister().catch(() => {
            // Станция может быть уже недоступна — отписка при закрытии вкладки не критична
        });
    }
    /** Принудительное обновление регистрации, например после восстановления транспорта */
    refreshNow() {
        if (!this.#running)
            return;
        void this.#register();
    }
    async #register() {
        if (!this.#running)
            return;
        this.#armLineDownTimer();
        try {
            await this.#registerer.register({ expiresSec: this.#expiresSec });
        }
        catch (error) {
            this.#options.onError?.(error);
            this.#onNotRegistered();
        }
    }
    #onRegistered() {
        this.#cancelLineDown?.();
        this.#cancelLineDown = null;
        this.#lineDown = false;
        this.#options.onLineUp();
        this.#armRefreshTimer();
    }
    #onNotRegistered() {
        this.#cancelRefresh?.();
        this.#cancelRefresh = null;
        this.#armLineDownTimer();
        // Повторную регистрацию пробуем на половине expires: чаще — лишний трафик
        // на станцию при её недоступности, реже — диспетчер простаивает.
        this.#armRefreshTimer();
    }
    #armRefreshTimer() {
        if (!this.#running)
            return;
        this.#cancelRefresh?.();
        const ratio = this.#options.refreshRatio ?? REGISTER_REFRESH_RATIO;
        this.#cancelRefresh = this.#scheduler.schedule(() => {
            this.#cancelRefresh = null;
            void this.#register();
        }, this.#expiresSec * 1000 * ratio);
    }
    #armLineDownTimer() {
        if (!this.#running || this.#cancelLineDown !== null)
            return;
        const after = this.#options.lineDownAfterMs ?? LINE_DOWN_AFTER_MS;
        this.#cancelLineDown = this.#scheduler.schedule(() => {
            this.#cancelLineDown = null;
            if (this.isRegistered())
                return;
            this.#lineDown = true;
            this.#options.onLineDown();
        }, after);
    }
    #clearTimers() {
        this.#cancelRefresh?.();
        this.#cancelRefresh = null;
        this.#cancelLineDown?.();
        this.#cancelLineDown = null;
    }
}
//# sourceMappingURL=registerer-supervisor.js.map